import { Sidebar } from "./Sidebar";
import { KaisoAgentPanel } from "./KaisoAgentPanel";
import { useAuth } from "@/contexts/AuthContext";
import { WhatsAppStatusContext } from "@/contexts/WhatsAppStatusContext";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/hooks/useAnalytics";
import { AlertTriangle, X, CreditCard, Menu, Bell, Crown, ArrowLeft, Wifi, WifiOff } from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { API_URL } from "@/utils/apiConfig";
import { playBellSound } from "@/lib/bellSound";
import { LanguageSelector } from "@/components/LanguageSelector";
import { setLanguageFromLocale } from "@/i18n";

const API = API_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_08f655cb-8b91-4c06-80bd-b94d5f4523ca/artifacts/kxmzqj5h_ChatGPT%20Image%2027%20de%20fev.%20de%202026%2C%2001_06_03.png";

// WhatsApp Disconnected Warning Banner — shown globally on all pages via MainLayout
const WhatsAppDisconnectedBanner = ({ loading, t }) => {
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Reset dismissed whenever the user navigates to a different page
  useEffect(() => {
    setDismissed(false);
  }, [location.pathname]);

  if (dismissed) return null;

  return (
    <div className="bg-red-600 text-white px-4 py-2.5 flex items-center justify-between gap-3 sticky top-12 z-30" data-testid="whatsapp-disconnected-banner">
      <div className="flex items-center gap-3">
        <WifiOff className="w-5 h-5 flex-shrink-0" />
        <p className="text-sm font-medium">
          <span className="font-bold">{t('admin.whatsappDisconnected')}</span> {t('admin.whatsappDisconnectedMsg')}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => navigate("/chatbot")}
          className="px-3 py-1 bg-white/20 rounded text-sm font-bold hover:bg-white/30 transition-colors flex items-center gap-1.5"
          data-testid="whatsapp-reconnect-btn"
          disabled={loading}
        >
          <Wifi className="w-3.5 h-3.5" /> {loading ? t('common.loading') : t('admin.reconnect')}
        </button>
        <button onClick={() => setDismissed(true)} className="p-1 hover:bg-white/20 rounded">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

const PaymentWarningBanner = ({ status, daysLeft, trialDaysLeft, t }) => {
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();
  const isTrial = status === "trial" && trialDaysLeft != null && trialDaysLeft <= 7;
  if (dismissed || (status !== "past_due" && status !== "suspended" && !isTrial)) return null;
  const isSuspended = status === "suspended";
  const isPastDue = status === "past_due";
  return (
    <div className={`${isSuspended ? "bg-red-600" : isPastDue ? "bg-amber-500" : "bg-blue-600"} text-white px-4 py-2.5 flex items-center justify-between gap-3`} data-testid="payment-warning-banner">
      <div className="flex items-center gap-3">
        <AlertTriangle className="w-5 h-5 flex-shrink-0" />
        <p className="text-sm font-medium">
          {isSuspended
            ? t('admin.accountSuspendedMsg')
            : isPastDue
            ? t('admin.paymentPendingMsg', { days: daysLeft })
            : trialDaysLeft === 0
            ? t('admin.trialExpiredMsg', { defaultValue: 'Tu prueba gratuita ha expirado. Suscríbete para continuar usando Kaiso.' })
            : t('admin.trialEndingSoonMsg', { days: trialDaysLeft, defaultValue: `Tu prueba termina en ${trialDaysLeft} día(s). Suscríbete para no perder acceso.` })}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button onClick={() => navigate("/settings?tab=subscription")} className="px-3 py-1 bg-white/20 rounded text-sm font-bold hover:bg-white/30 transition-colors flex items-center gap-1.5" data-testid="payment-warning-action">
          <CreditCard className="w-3.5 h-3.5" /> {isSuspended ? t('admin.reactivate') : isPastDue ? t('admin.payNow') : t('admin.viewPlans', { defaultValue: 'Ver Planos' })}
        </button>
        {!isSuspended && <button onClick={() => setDismissed(true)} className="p-1 hover:bg-white/20 rounded"><X className="w-4 h-4" /></button>}
      </div>
    </div>
  );
};

export const MainLayout = ({ children }) => {
  const { authAxios, user, isImpersonating: jwtImpersonating } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const { t } = useTranslation();
  const [subStatus, setSubStatus] = useState(null);
  const [daysLeft, setDaysLeft] = useState(null);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [newOrderCount, setNewOrderCount] = useState(0);
  const [newReservationCount, setNewReservationCount] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [whatsappConnected, setWhatsappConnected] = useState(null); // null = not yet polled
  const [chatbotEnabled, setChatbotEnabled] = useState(false);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [restaurant, setRestaurant] = useState(null);
  useAnalytics(restaurant);
  const prevCountRef = useRef(0);
  const hiddenAtRef = useRef(null); // track when tab was hidden
  const isImpersonating = jwtImpersonating || !!localStorage.getItem("admin_original_token");
  const navigate = useNavigate();
  const location = useLocation();
  const isSettingsPage = location.pathname === "/settings" || location.pathname.startsWith("/settings");
  // Pages that embed WABanner themselves — suppress the global duplicate
  const hasEmbeddedWABanner = location.pathname === "/orders" || location.pathname === "/pos";
  const originalToken = localStorage.getItem("admin_original_token");

  // Close sidebar on route change (fixes dark screen bug on mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const returnToAdmin = () => {
    if (originalToken) {
      localStorage.setItem("token", originalToken);
      // Restore original refresh token too
      const originalRefresh = localStorage.getItem("admin_original_refresh");
      if (originalRefresh) {
        localStorage.setItem("refresh_token", originalRefresh);
        localStorage.removeItem("admin_original_refresh");
      }
      localStorage.removeItem("admin_original_token");
      window.location.href = "/platform-admin";
    }
  };

  // Fetch restaurant info + subscription access
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refreshSubStatus = useCallback(() => {
    authAxiosRef.current.get("/subscription/check-access").then(res => {
      setSubStatus(res.data?.subscription_status);
      setDaysLeft(res.data?.days_until_suspension);
      setTrialDaysLeft(res.data?.trial_days_left ?? null);
    }).catch(() => {
      authAxiosRef.current.get("/restaurant").then(res => {
        setSubStatus(res.data?.subscription_status);
        setDaysLeft(res.data?.days_until_suspension);
      }).catch(() => {});
    });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    authAxiosRef.current.get("/restaurant").then(res => {
      setRestaurant(res.data);
      if (res.data?.locale) setLanguageFromLocale(res.data.locale);
    }).catch(() => {});
    refreshSubStatus();
    // Re-check every 5 min so suspended/past_due/trial-expiry shows without reload
    const interval = setInterval(refreshSubStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [refreshSubStatus]);

  // Check WhatsApp connection status periodically.
  // Separation of concerns: chatbotEnabled is fetched once at mount (stable config),
  // whatsappConnected is polled every 30s (live status). Banner shows only when
  // chatbotEnabled=true AND whatsappConnected=false (not null = not yet loaded).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pollWhatsAppStatus = useCallback(async () => {
    try {
      const res = await authAxiosRef.current.get("/chatbot/whatsapp/status");
      setWhatsappConnected(res.data?.status === "connected");
    } catch {
      // keep previous state on transient errors
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const checkWhatsAppStatus = async () => {
      try {
        const [statusRes, restaurantRes] = await Promise.allSettled([
          authAxiosRef.current.get("/chatbot/whatsapp/status"),
          authAxiosRef.current.get("/restaurant"),
        ]);
        if (restaurantRes.status === "fulfilled") {
          setChatbotEnabled(restaurantRes.value.data?.chatbot_enabled === true);
        }
        if (statusRes.status === "fulfilled") {
          setWhatsappConnected(statusRes.value.data?.status === "connected");
        }
        setWhatsappLoading(false);
      } catch {
        setWhatsappLoading(false);
      }
    };

    checkWhatsAppStatus();
    const interval = setInterval(pollWhatsAppStatus, 15000);
    // Re-poll immediately when tab regains focus — avoids showing stale "Desconectado"
    // after the user switches away and comes back while a reconnect completed.
    const onVisible = () => { if (document.visibilityState === 'visible') pollWhatsAppStatus(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisible); };
  }, [pollWhatsAppStatus]);

  // Poll new order count - only if restaurant has basic setup
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchOrderCount = useCallback(async () => {
    try {
      const res = await authAxiosRef.current.get("/restaurant");
      const rid = res.data?.id;
      const hasProducts = (res.data?.product_count || res.data?.total_products || 0) >= 1;
      const hasName = !!res.data?.name;
      // Don't poll for orders if restaurant isn't set up yet
      if (!rid || !hasName) {
        setNewOrderCount(0);
        return;
      }
      const r = await authAxiosRef.current.get(`/push/new-orders-count?restaurant_id=${rid}`);
      const count = r.data?.count || 0;
      if (count > prevCountRef.current && prevCountRef.current >= 0) {
        try {
          playBellSound(2); // Play bell sound 2 times for new orders
        } catch {}
      }
      prevCountRef.current = count;
      setNewOrderCount(count);
    } catch {}
  }, []);

  useEffect(() => {
    fetchOrderCount();
    const i = setInterval(fetchOrderCount, 10000);
    return () => clearInterval(i);
  }, [fetchOrderCount]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchReservationCount = useCallback(async () => {
    try {
      const r = await authAxiosRef.current.get("/reservations/pending-count");
      setNewReservationCount(r.data?.count || 0);
    } catch {}
  }, []);

  useEffect(() => {
    fetchReservationCount();
    const i = setInterval(fetchReservationCount, 30000);
    return () => clearInterval(i);
  }, [fetchReservationCount]);

  // Visibility-aware polling: pause when tab hidden, refetch immediately on return after >5 min
  useEffect(() => {
    const STALE_THRESHOLD = 5 * 60 * 1000; // 5 minutes
    const handleVisibility = () => {
      if (document.hidden) {
        hiddenAtRef.current = Date.now();
      } else {
        const hiddenDuration = hiddenAtRef.current ? Date.now() - hiddenAtRef.current : 0;
        hiddenAtRef.current = null;
        if (hiddenDuration >= STALE_THRESHOLD) {
          fetchOrderCount();
          fetchReservationCount();
          pollWhatsAppStatus();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [fetchOrderCount, fetchReservationCount, pollWhatsAppStatus]);

  // Register push subscription
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user?.id || !("serviceWorker" in navigator) || !("PushManager" in window)) return;

    const registerPush = async () => {
      try {
        const reg = await navigator.serviceWorker.register("/sw-push.js");
        const existing = await reg.pushManager.getSubscription();
        if (existing) {
          setPushEnabled(true);
          return;
        }

        const permission = await Notification.requestPermission();
        if (permission !== "granted") return;

        // Fetch VAPID key from backend
        const vapidRes = await fetch(`${API}/api/v1/push/vapid-key`);
        const vapidBody = await vapidRes.json();
        const vapidData = vapidBody?.data ?? vapidBody;
        const public_key = vapidData?.public_key;
        if (!public_key) return;

        const subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(public_key),
        });

        const res = await authAxiosRef.current.get("/restaurant");
        const restaurantId = res.data?.id;
        if (!restaurantId) return;

        await authAxiosRef.current.post("/push/subscribe-admin", {
          user_id: user.id,
          restaurant_id: restaurantId,
          subscription: subscription.toJSON(),
        });

        setPushEnabled(true);
      } catch (e) {
        console.warn("Push registration failed:", e);
      }
    };

    const timer = setTimeout(registerPush, 2000);
    return () => clearTimeout(timer);
  }, [user]);

  return (
    <WhatsAppStatusContext.Provider value={whatsappConnected ?? true}>
    <div className="min-h-screen bg-background">

      {/* Top bar */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center justify-between px-3 h-12">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 -ml-1 rounded-lg hover:bg-gray-100 transition" data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5 text-gray-700" />
          </button>
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Kaiso" className="w-7 h-7 rounded-md object-cover" />
            <span className="text-sm font-bold text-gray-800">Kaiso <span className="text-emerald-600">System</span></span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSelector variant="minimal" />
            {newOrderCount > 0 && (
              <div className="relative">
                <Bell className="w-5 h-5 text-emerald-600" />
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center" data-testid="mobile-order-badge">{newOrderCount}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} newOrderCount={newOrderCount} newReservationCount={newReservationCount} pushEnabled={pushEnabled} />

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-[45]" onClick={() => setSidebarOpen(false)} data-testid="sidebar-overlay" />
      )}

      <main className="pt-12 overflow-x-hidden max-w-full">
        {isImpersonating && (
          <div className="bg-amber-500 text-white px-4 py-2.5 flex items-center justify-between gap-3" data-testid="impersonation-banner">
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5" />
              <p className="text-sm font-bold">{t('admin.adminMasterMode')} — {t('admin.viewingPanelOf')}: {user?.name || user?.email}</p>
            </div>
            <button onClick={returnToAdmin} className="px-4 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-bold transition-colors flex items-center gap-1.5" data-testid="return-to-admin-btn">
              <ArrowLeft className="w-3.5 h-3.5" /> {t('admin.returnToAdmin')}
            </button>
          </div>
        )}
        {subStatus === "suspended" && !isSettingsPage && (
          <div className="p-4 sm:p-6 lg:p-8">
            <div className="max-w-lg mx-auto text-center py-20 space-y-4" data-testid="suspended-overlay">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">{t('admin.accountSuspended')}</h2>
              <p className="text-gray-500">{t('admin.accountSuspendedDesc')}</p>
              <button
                onClick={() => navigate("/settings?tab=subscription")}
                className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold transition-colors inline-flex items-center gap-2"
                data-testid="suspended-subscribe-btn"
              >
                <CreditCard className="w-4 h-4" /> {t('admin.viewPlansAndSubscribe')}
              </button>
            </div>
          </div>
        )}
        {(subStatus !== "suspended" || isSettingsPage) && (
          <>
            <PaymentWarningBanner status={subStatus} daysLeft={daysLeft} trialDaysLeft={trialDaysLeft} t={t} />
            {chatbotEnabled && whatsappConnected === false && !hasEmbeddedWABanner && (
              <WhatsAppDisconnectedBanner loading={whatsappLoading} t={t} />
            )}
            <div className="p-4 sm:p-6 lg:p-8">{children}</div>
          </>
        )}
      </main>
      <KaisoAgentPanel />
    </div>
    </WhatsAppStatusContext.Provider>
  );
};
